/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect, useRef, useState } from "react";
import { IoMdClose } from "react-icons/io";
import { toast } from "react-toastify";
import { ICategoryType, ILearnisticTag } from "../../generated/Api";
import useClickOutside from "../../hook/useClickOutside";
import { ITag } from "../../types/TagTypes";
import { getScrollParent } from "../../utils/DOMHelper";
import { default as IconBerserkerMail } from "../Icon";
import StyledButton from "../StyledButton";
import { AutoCompleteSelectItem } from "./AutoCompleteItem";

const Form = styled.div`
  width: 100%;
  display: flex;
  position: relative;
  font-family: "Source Sans Pro" !important;
`;

const InputContainer = styled.div`
  width: 100%;
`;

const StyledIcon = styled.div`
  position: absolute;
  left: 10px;
  top: 13px;
`;

const StyledClearIcon = styled.div<{ isCreatingTag: boolean }>`
  position: absolute;
  cursor: pointer;
  right: ${(props) => (props.isCreatingTag ? "80px" : "14px")};
  top: 10px;
  height: 22px;
  width: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s;

  :hover {
    background-color: #22222217;
  }
`;

interface InputProps {
  active: boolean;
}

const Input = styled.input<InputProps>`
  width: 100%;
  height: 42px;
  border: 1px solid #dddddd;
  border-radius: 5px;
  outline: none;
  font-size: 16px;
  padding: 5px;
  color: #383838;
  text-indent: 25px;

  :focus {
    box-shadow: 0px 0px 6px #fb972e4d;
    border: 1px solid #fb972e;
  }

  ${(props) =>
    props.active &&
    css`
      box-shadow: 0px 0px 6px #fb972e4d;
      border: 1px solid #fb972e;
    `}

  &::placeholder {
    color: #979797;
  }
  background-color: #ffffff;
`;

const AutoCompleteDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  border-radius: 5px;
  background-color: #ffffff;
`;

interface DropViewProps {
  position: { top: number; left: number };
  width: number;
  offset: number;
}

const StyledAutocompleteList = styled.div<DropViewProps>`
  position: fixed;
  background-color: #fff;
  z-index: 15;
  max-height: 320px;
  flex: 1;
  overflow: auto;
  border-radius: 4px;
  box-shadow: 0px 3px 6px #00000033;
  border: 1px solid #d4d4d4;
  border-bottom: none;
  border-top: none;
  right: 0;
  top: ${(props) =>
    props.offset > 0 ? `auto` : `${props.position.top + 5}px`};
  left: ${(props) => props.position.left}px;
  width: ${(props) => props.width}px;
  bottom: ${(props) => (props.offset > 0 ? `${props.offset + 5}px` : "auto")};

  @media (max-height: 700px) {
    max-height: 260px;
  }

  @media (max-height: 600px) {
    max-height: 240px;
  }
`;

interface IListItemContainer {
  isActive: boolean;
}

const GroupName = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
  padding-left: 15px;
  font-size: 16px;
  font-weight: 600;
  color: #000;
  background-color: #fff;
`;

const NoResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #fff;
  color: #000;
  padding: 16px;
`;
const IconContainer = styled.div``;
const Title = styled.div`
  margin-top: 10px;
  font-weight: bold;
  margin-bottom: 12px;
`;
const Content = styled.div`
  color: #666666;
  margin-bottom: 5px;
`;

const CreateTagButton = styled(StyledButton)`
  margin-top: 12px;
`;

export const SystemTag = styled.div`
  font-size: 11px;
  background-color: #eceeef;
  color: #9d9e9e;
  padding: 4px;
  margin-left: 10px;
`;
const GroupContainer = styled.div`
  display: flex;
  align-items: center;
  background-color: #fff;
  :hover {
    background-color: #fff;
  }
`;

const CreateTag = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 10px;
  cursor: pointer;
  border-bottom: 1px solid #ebebeb;
  background-color: #fff2e5;
  color: #fb972e;
  height: fit-content;

  &:hover {
    background-color: #fb972e;
    color: #fff;

    svg {
      path {
        fill: #fff;
      }
    }
  }
`;

const CreateTagTitle = styled.span`
  font-size: 16px;
  margin-left: 5px;
  font-weight: normal;
  display: flex;
  align-items: center;
  width: 100%;
  white-space: pre-line;

  b {
    margin: 0 3px;
    width: 100%;
    white-space: pre;
  }
`;

export interface ITagOption extends ITag {}

type IStyledAutocompleteSelectItemProps = {
  category?: string;
  type?: string;
  value: string;
  icon?: string;
  suffix?: string;
  index: number;
  isActive: boolean;
};

type IStyledAutocompleteSelectProps = {
  placeholder?: string;
  suggestions: ILearnisticTag[] | undefined;
  selectedValue: string;
  onEnter: (tag: ILearnisticTag) => void;
  onValueChange?: (value: string) => void;
  onValueSelect: (tag: ILearnisticTag) => void;
  viewCategories?: () => void;
};

export default function AutoCompleteTag(
  props: IStyledAutocompleteSelectProps
): JSX.Element {
  const {
    suggestions,
    onEnter,
    selectedValue,
    onValueSelect,
    onValueChange,
    placeholder,
    viewCategories,
  } = props;
  const [currentFocus, setCurrentFocus] = useState(0);

  const [currentSuggestions, setCurrentSuggestions] =
    useState<IStyledAutocompleteSelectItemProps[]>();

  const [groups, setGroups] =
    useState<Array<{ category: string | undefined; type: ICategoryType }>>();
  /**
   * Select functionality
   */
  const [openDropView, setOpenDropView] = useState<boolean>(false);
  /**
   * Refs to keep track
   */
  const wrapperRef = useClickOutside(() => setOpenDropView(false));
  const [offsetPopover, setOffsetPopover] = useState(0);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const dropViewRef = useRef<HTMLDivElement | null>(null);

  const suggestionRefMap = useRef<{
    [suggestionValue: string]: HTMLDivElement | null;
  }>({});

  const [dropViewPosition, setDropViewPosition] = useState<{
    top: number;
    left: number;
  }>({
    top: 0,
    left: 0,
  });
  const [dropViewWidth, setDropViewWidth] = useState<number>(0);
  const [tagToCreate, setTagToCreate] = useState<string>("");
  /**
   * Update DropView position to match current Input location on screen.
   *
   * This function moves the DropView below the Input position.
   */
  function updateDropViewPosition() {
    const elementBoundingRect = (
      wrapperRef.current as HTMLDivElement
    ).getBoundingClientRect();

    setDropViewPosition({
      top: elementBoundingRect.top + elementBoundingRect.height,
      left: elementBoundingRect.left,
    });

    setDropViewWidth(elementBoundingRect.width);
  }

  useEffect(() => {
    if (!dropViewRef.current || !wrapperRef.current) return;

    const elementBoundingRect = (
      dropViewRef.current as HTMLDivElement
    ).getBoundingClientRect();

    const containerBoundingRect = (
      wrapperRef.current as HTMLDivElement
    ).getBoundingClientRect();

    let bottom = elementBoundingRect.bottom;

    if (offsetPopover !== 0)
      bottom =
        bottom + elementBoundingRect.height + containerBoundingRect.height;

    if (bottom >= 0 && bottom >= window.innerHeight) {
      setOffsetPopover(window.innerHeight - containerBoundingRect.top);
    } else {
      setOffsetPopover(0);
    }
  }, [dropViewRef, openDropView, selectedValue]);

  /**
   * useEffect that closes the DropView when a scroll is detected, much like
   * the native <select />
   */
  useEffect(() => {
    if (!openDropView) {
      setCurrentFocus(0);
      return () => {};
    }

    function closeDropView() {
      setOpenDropView(false);
    }

    const scrollParent = getScrollParent(
      containerRef.current,
      containerRef.current
    );

    if (!scrollParent) return () => {};

    scrollParent.addEventListener("scroll", closeDropView);

    return () => {
      scrollParent.removeEventListener("scroll", closeDropView);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapperRef, openDropView]);

  function handleFilterSuggestion() {
    if (suggestions) {
      const suggestionsArray = suggestions
        .map((suggestion, index) => ({
          ...suggestion,
          value: suggestion.name,
          index,
          isActive: false,
        }))
        .filter((suggestion) => {
          return suggestion.value
            .toLocaleLowerCase()
            .replace(/\s\s+/g, " ")
            .trim()
            .includes(
              selectedValue.toLocaleLowerCase().replace(/\s\s+/g, " ").trim()
            );
        });

      setCurrentSuggestions(suggestionsArray);
    }
    updateDropViewPosition();
  }

  useEffect(() => {
    handleFilterSuggestion();
  }, [suggestions, selectedValue]);

  /**
   * On window resize, calculate position
   */
  useEffect(() => {
    window.addEventListener("resize", updateDropViewPosition);
    return () => window.removeEventListener("resize", updateDropViewPosition);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCurrentSuggestions((prevSuggestionList) => {
      if (prevSuggestionList) {
        return prevSuggestionList.map((suggestionItem, index) => {
          if (index === currentFocus) {
            return {
              ...suggestionItem,
              isActive: true,
            };
          }
          return {
            ...suggestionItem,
            isActive: false,
          };
        });
      }
      return undefined;
    });
  }, [currentFocus]);

  function typedValidValue(): ILearnisticTag | undefined {
    if (suggestions && selectedValue) {
      const tag = suggestions.find((suggestion) => {
        return suggestion.name === selectedValue;
      });
      return tag;
    }
    return undefined;
  }

  function handleLoseFocus() {
    const tag = typedValidValue();
    // The value he typed is already a value in the suggestion list
    if (tag) {
      setOpenDropView(false);
      onValueSelect(tag);
    }
  }

  function handleItemClick(item: IStyledAutocompleteSelectItemProps) {
    if (suggestions) {
      const tag = suggestions.find(
        (suggestion) => suggestion.name === item.value
      ) as ILearnisticTag;

      setOpenDropView(false);

      onValueSelect(tag);
    }
  }

  function handleInputKeyDown(e: React.KeyboardEvent) {
    if (suggestions && currentSuggestions) {
      const lastItem = currentSuggestions.length - 1;
      if (e.key === "ArrowDown") {
        e.preventDefault();

        let currentIndex = 0;

        if (currentFocus === lastItem) {
          setCurrentFocus(0);
        } else {
          setCurrentFocus(currentFocus + 1);
          currentIndex = currentFocus + 1;
        }

        let currentSuggestionActive = currentSuggestions[currentIndex];

        suggestionRefMap.current[currentSuggestionActive.value]?.scrollIntoView(
          {
            behavior: "smooth",
            block: "center",
          }
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();

        let currentIndex = lastItem;
        if (currentFocus === 0) {
          setCurrentFocus(lastItem);
        } else {
          setCurrentFocus(currentFocus - 1);
          currentIndex = currentFocus - 1;
        }

        let currentSuggestionActive = currentSuggestions[currentIndex];

        suggestionRefMap.current[currentSuggestionActive.value]?.scrollIntoView(
          {
            behavior: "auto",
            block: "nearest",
            inline: "start",
          }
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (currentFocus > -1) {
          if (currentSuggestions[currentFocus]) {
            const tag = suggestions.find(
              //@ts-ignore
              (s) => s._id === currentSuggestions[currentFocus]._id
            ) as ILearnisticTag;
            setOpenDropView(false);

            onEnter(tag);
          }
        } else {
          const tag = typedValidValue();
          if (tag) {
            setOpenDropView(false);

            onEnter(tag);
          }
        }
      }
    }
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    setCurrentFocus(-1);
    setOpenDropView(true);

    if (onValueChange) onValueChange(value);
  }

  function handleNewTag(tagName: string) {
    if (!viewCategories) return;

    if (tagName.trim().length > 0) {
      setTagToCreate(tagName);
      viewCategories();
    } else {
      toast("Tag name can't be empty!", { type: "error" });
    }
  }

  const ResultScreen = () => {
    return (
      <NoResultsContainer>
        <IconContainer>
          <IconBerserkerMail name="noResultsFound" />
          <Title>No Results Found</Title>
        </IconContainer>
      </NoResultsContainer>
    );
  };

  const NoTagsScreen = () => {
    return (
      <NoResultsContainer>
        <IconContainer>
          <IconBerserkerMail name="noResultsFound" />
          <Title>No Results Found</Title>
        </IconContainer>
        <Content>Your account does not have any tags.</Content>
      </NoResultsContainer>
    );
  };

  function clearInput() {
    if (!onValueChange) return;
    onValueChange("");
  }

  const userTags = suggestions?.filter((s) => s.category !== "course") || [];
  const courseTags = suggestions?.filter((s) => s.category === "course") || [];

  return (
    <Form>
      <AutoCompleteDiv ref={containerRef}>
        <InputContainer ref={wrapperRef}>
          <Input
            active={openDropView}
            onFocus={() => {
              handleFilterSuggestion();
              setOpenDropView(true);
            }}
            onClick={() => {
              handleFilterSuggestion();
              setOpenDropView(true);
            }}
            onBlur={handleLoseFocus}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            value={selectedValue}
            type="text"
            placeholder={placeholder}
            autoComplete="off"
          />
          {openDropView && (
            <StyledAutocompleteList
              offset={offsetPopover}
              ref={dropViewRef}
              position={dropViewPosition}
              width={dropViewWidth}
            >
              {suggestions && suggestions.length > 0 ? (
                currentSuggestions?.length ? (
                  <>
                    {userTags && userTags.length > 0 && (
                      <>
                        <GroupContainer>
                          <GroupName>Tags</GroupName>
                        </GroupContainer>
                        <div>
                          {currentSuggestions &&
                            currentSuggestions
                              .filter((f) => f.category !== "course")
                              .map((suggestionItem) => {
                                return (
                                  <div
                                    ref={(r) =>
                                      (suggestionRefMap.current[
                                        suggestionItem.value
                                      ] = r)
                                    }
                                  >
                                    <AutoCompleteSelectItem
                                      key={
                                        suggestionItem.index +
                                        suggestionItem.value
                                      }
                                      onClick={() =>
                                        handleItemClick(suggestionItem)
                                      }
                                      name={suggestionItem.value
                                        .replace(/\s\s+/g, " ")
                                        .trim()}
                                      isActive={suggestionItem.isActive}
                                      alreadyWritten={selectedValue
                                        .replace(/\s\s+/g, " ")
                                        .trim()}
                                      customStyle={{ paddingLeft: "30px" }}
                                    />
                                  </div>
                                );
                              })}
                        </div>
                      </>
                    )}
                    {courseTags && courseTags.length > 0 && (
                      <>
                        <GroupContainer>
                          <GroupName>Courses</GroupName>
                        </GroupContainer>
                        <div>
                          {currentSuggestions &&
                            currentSuggestions
                              .filter((f) => f.category === "course")
                              .map((suggestionItem) => {
                                return (
                                  <div
                                    ref={(r) =>
                                      (suggestionRefMap.current[
                                        suggestionItem.value
                                      ] = r)
                                    }
                                  >
                                    <AutoCompleteSelectItem
                                      key={
                                        suggestionItem.index +
                                        suggestionItem.value
                                      }
                                      onClick={() =>
                                        handleItemClick(suggestionItem)
                                      }
                                      name={suggestionItem.value
                                        .replace(/\s\s+/g, " ")
                                        .trim()}
                                      isActive={suggestionItem.isActive}
                                      alreadyWritten={selectedValue
                                        .replace(/\s\s+/g, " ")
                                        .trim()}
                                      customStyle={{ paddingLeft: "30px" }}
                                    />
                                  </div>
                                );
                              })}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <ResultScreen />
                )
              ) : (
                <NoTagsScreen />
              )}
            </StyledAutocompleteList>
          )}
        </InputContainer>
      </AutoCompleteDiv>
      <StyledIcon>
        <IconBerserkerMail name="search" height={16} width={16} />
      </StyledIcon>
      {selectedValue.length > 0 && (
        <StyledClearIcon
          isCreatingTag={!!tagToCreate.length}
          onClick={(e) => {
            e.stopPropagation();
            clearInput();
          }}
        >
          <IoMdClose color="#777777" size={16} />
        </StyledClearIcon>
      )}
    </Form>
  );
}
